import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

interface PolicySection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

interface Policy {
  title: string;
  effectiveDate: string;
  intro: string;
  sections: PolicySection[];
}
@Component({
  selector: 'app-legal-policy',
  templateUrl: './legal-policy.component.html',
  styleUrl: './legal-policy.component.scss'
})
export class LegalPolicyComponent implements OnInit, OnDestroy {

  policyKey = '';
  policy?: Policy;
  private routeSubscription?: Subscription;

  readonly contact = {
    email: 'support@budgetbazaar.in',
    phone: '81100 07090',
    phoneLink: '+918110007090',
    business: 'Shree Thangam Textiles, Budget Bazaar',
    address: 'Maniram Mahal, Near Cine Priya Theater Side Gate, Kuruvikaran 2nd Street, Annanagar, Madurai - 625020',
    website: 'https://budgetbazaar.store/'
  };

  readonly policies: Record<string, Policy> = {
    terms: {
      title: 'Terms and Conditions',
      effectiveDate: '22 October 2026',
      intro: 'Welcome to Budget Bazaar. These Terms and Conditions govern your access to and use of our website and services. By accessing our website or placing an order, you agree to these terms. If you do not agree, please discontinue using our website.',
      sections: [
        {
          heading: '1. About Budget Bazaar',
          paragraphs: ['Budget Bazaar is an online shopping platform that allows customers to browse and purchase products listed on our website. Product availability, delivery options, prices and services may vary depending on location and stock availability.']
        },
        {
          heading: '2. Eligibility and User Account',
          paragraphs: ['You must be legally capable of entering into a binding agreement to place an order. You agree to provide accurate, complete and up-to-date information during registration and checkout. You are responsible for maintaining the confidentiality of your account credentials and for activities carried out through your account.']
        },
        {
          heading: '3. Products and Pricing',
          paragraphs: [
            'We make reasonable efforts to display accurate product descriptions, images, specifications and prices. Colours, packaging and appearance may vary from displayed images. Prices are shown in Indian Rupees (INR), unless otherwise stated, and applicable taxes and delivery charges will be displayed where relevant.',
            'We reserve the right to correct pricing errors, update product information, modify offers or discontinue products. If an order is affected by a material error, we may contact you to resolve the issue or cancel the affected order and refund any amount paid, as applicable.'
          ]
        },
        {
          heading: '4. Orders and Acceptance',
          paragraphs: [
            'Placing an order constitutes a purchase request. An order confirmation does not necessarily mean that the order has been finally accepted or dispatched. Orders are subject to product availability, payment verification, delivery serviceability and applicable checks.',
            'We may reject or cancel an order for legitimate reasons, including stock unavailability, suspected fraud, incorrect pricing or inaccurate customer information. Where payment has already been collected for a cancelled order, the applicable refund will be initiated.'
          ]
        },
        {
          heading: '5. Payments',
          paragraphs: [
            'Payments must be made using the payment methods offered during checkout. You agree to provide accurate billing and payment information. Orders may be held or cancelled if payment is unsuccessful or cannot be verified.',
            'We do not intentionally store complete payment card credentials unless handled through an appropriately secured and authorized payment arrangement.'
          ]
        },
        {
          heading: '6. Shipping and Delivery',
          paragraphs: ['Delivery estimates are provided for guidance and are not guaranteed unless expressly stated. Delivery may be affected by location, weather, logistics, public holidays or circumstances beyond our reasonable control. Customers must provide a complete and accurate delivery address and contact number.']
        },
        {
          heading: '7. Cancellation, Returns and Refunds',
          paragraphs: ['Order cancellations, returns, exchanges and refunds are governed by our separate Cancellation Policy and Refund Policy. Certain products may be ineligible for return due to their nature, hygiene requirements, expiry, customization or applicable legal restrictions. Restrictions will be communicated where applicable and will not override rights provided by law.']
        },
        {
          heading: '8. Prohibited Activities',
          paragraphs: ['You agree not to misuse the website, submit false information, attempt unauthorized access, interfere with website operations, use the platform for unlawful purposes or engage in fraudulent transactions.']
        },
        {
          heading: '9. Intellectual Property',
          paragraphs: ['Website content, including text, logos, graphics, product presentation and design elements, may be protected by intellectual property laws. You may not reproduce, distribute or commercially exploit such content without the necessary authorization.']
        },
        {
          heading: '10. Limitation of Liability',
          paragraphs: ['To the extent permitted by applicable law, Budget Bazaar shall not be liable for indirect or consequential losses arising from website use, temporary service interruptions or delivery delays beyond our reasonable control. Nothing in these terms excludes liability that cannot legally be excluded or limits non-waivable consumer rights.']
        },
        {
          heading: '11. Privacy',
          paragraphs: ['Your personal information is handled in accordance with our Privacy Policy. By using our services, you acknowledge that personal information may be processed as described in that policy and as permitted by applicable law.']
        },
        {
          heading: '12. Changes to These Terms',
          paragraphs: ['We may update these Terms and Conditions from time to time. The revised version will be published on this page with an updated effective date. Continued use of the website after changes take effect constitutes acceptance to the extent permitted by law.']
        },
        {
          heading: '13. Governing Law',
          paragraphs: ['These terms are governed by the laws of India. Disputes will be subject to the jurisdiction of the competent courts, subject to applicable consumer protection laws and other mandatory legal provisions.']
        }
      ]
    },

    privacy: {
      title: 'Privacy Policy',
      effectiveDate: '22 October 2026',
      intro: 'Budget Bazaar respects your privacy and is committed to handling your personal information responsibly. This Privacy Policy explains how we collect, use, store, share and protect information when you visit our website or use our shopping services.',
      sections: [
        {
          heading: '1. Information We Collect',
          paragraphs: ['Depending on how you use our website, we may collect the following information:'],
          bullets: [
            'Personal information: name, mobile number, email address, delivery address and billing address.',
            'Order information: products purchased, order history, quantities, order status and transaction references.',
            'Payment information: payment status and transaction details received from payment service providers. Complete card or banking credentials are generally handled by the relevant payment provider.',
            'Technical information: IP address, browser type, device information, website activity and diagnostic logs.',
            'Communication information: messages, feedback, complaints and customer support requests.'
          ]
        },
        {
          heading: '2. How We Use Your Information',
          bullets: [
            'Process and deliver your orders.',
            'Verify payments and prevent fraudulent transactions.',
            'Send order confirmations, invoices, delivery updates and service notifications.',
            'Respond to questions, complaints, returns and refund requests.',
            'Improve our website, products and customer experience.',
            'Send promotional communications where permitted and, where required, with your consent.',
            'Comply with applicable laws, tax requirements and lawful requests.'
          ]
        },
        {
          heading: '3. Cookies and Similar Technologies',
          paragraphs: ['Our website may use cookies or similar technologies to support essential functionality, remember preferences, understand website usage and improve performance. You can manage cookies through your browser settings. Disabling certain cookies may affect website functionality.']
        },
        {
          heading: '4. Sharing of Information',
          paragraphs: ['We do not sell your personal information to third parties. We may share necessary information with trusted service providers, including:'],
          bullets: [
            'Payment gateways and financial service providers.',
            'Delivery partners and logistics companies.',
            'Website hosting, technical support and security providers.',
            'Customer communication and notification services.',
            'Professional advisers, regulators or law enforcement authorities where legally required.'
          ]
        },
        {
          heading: '5. Data Security',
          paragraphs: ['We use reasonable technical and organizational safeguards to protect personal information against unauthorized access, misuse, alteration, loss or disclosure. However, no electronic transmission or storage system can be guaranteed to be completely secure.']
        },
        {
          heading: '6. Data Retention',
          paragraphs: ['We retain personal information only for as long as reasonably necessary to fulfil the purposes described in this policy, resolve disputes, maintain business records and comply with applicable legal obligations. Information may be retained longer where required by law.']
        },
        {
          heading: '7. Your Privacy Rights',
          paragraphs: ['Subject to applicable law, you may request access to, correction of or deletion of your personal information. You may also withdraw consent where processing is based on consent and exercise other applicable privacy rights. Certain records may need to be retained for legal or legitimate business requirements. We may need to verify your identity before processing a request.']
        },
        {
          heading: '8. Marketing Communications',
          paragraphs: ['You may opt out of promotional emails or messages using the unsubscribe option where available or by contacting us. Essential communications related to orders, payments, security and customer service may still be sent.']
        },
        {
          heading: "9. Children's Privacy",
          paragraphs: ['Our website is not intended to encourage children to independently enter into purchase transactions where they are not legally eligible to do so. Personal information relating to children will be handled in accordance with applicable law.']
        },
        {
          heading: '10. Third-Party Websites and Services',
          paragraphs: ['Our website may use or link to third-party payment, delivery or other services. Those providers may have their own privacy policies. Budget Bazaar is not responsible for independent privacy practices outside its control.']
        },
        {
          heading: '11. Changes to This Policy',
          paragraphs: ['We may revise this Privacy Policy periodically. Any changes will be published on this page with an updated effective date.']
        }
      ]
    },

    refund: {
      title: 'Refund Policy',
      effectiveDate: '22 October 2026',
      intro: 'At Budget Bazaar, we aim to provide a reliable and satisfactory shopping experience. This Refund Policy explains when a refund may be available and how refund requests are handled for purchases made through our website.',
      sections: [
        {
          heading: '1. Eligibility for Refunds',
          paragraphs: ['A refund may be considered in the following circumstances, subject to verification and applicable law:'],
          bullets: [
            'The order is cancelled by Budget Bazaar because a product is unavailable.',
            'Payment is successfully collected but the order is not accepted or cannot be fulfilled.',
            'The wrong product is delivered.',
            'A product arrives damaged, defective or materially different from its description.',
            'An eligible order is cancelled or returned in accordance with applicable policies.',
            'A duplicate payment or verified payment-processing error occurs.'
          ]
        },
        {
          heading: '2. Products That May Not Be Eligible',
          paragraphs: ['Certain products may not be eligible for return or refund after delivery, particularly where the product is perishable, opened, used, customized or subject to hygiene or safety restrictions. These restrictions do not prevent a valid complaint regarding a defective, damaged, incorrect, unsafe or legally non-compliant product. Exclusions remain subject to applicable law.']
        },
        {
          heading: '3. Reporting Damaged, Defective or Incorrect Products',
          paragraphs: ['Contact our customer support team as soon as reasonably possible after receiving the product. Provide the following details:'],
          bullets: [
            'Order number.',
            'Product name and quantity.',
            'Description of the issue.',
            'Photographs or other supporting evidence, where reasonably available.'
          ]
        },
        {
          heading: '4. Refund Request Review',
          paragraphs: ['Our team will review the order details and available evidence. We may contact you for clarification or arrange an inspection or return where appropriate. Once a refund is approved, we will initiate it through the original payment method where supported, or another mutually agreed lawful method.']
        },
        {
          heading: '5. Refund Processing Time',
          paragraphs: ['Approved refunds will be initiated within a reasonable processing period. The time taken for funds to appear in your account depends on the payment gateway, bank, card issuer or other payment service provider. If the refund has been initiated but has not appeared within the expected timeframe, contact us with your order number and available payment reference.']
        },
        {
          heading: '6. Shipping and Delivery Charges',
          paragraphs: ['Delivery charges and other fees will be reviewed according to the reason for the refund and applicable law. Where a return or refund is required because of an error attributable to Budget Bazaar, eligible charges will be handled in accordance with applicable requirements. For other returns, the treatment of shipping charges will be communicated during the return process and will depend on the circumstances.']
        },
        {
          heading: '7. Cash on Delivery Orders',
          paragraphs: ['For eligible Cash on Delivery orders, refunds may be processed through a supported bank transfer or another agreed refund method. Customers may be asked to provide the necessary details securely to complete the refund.']
        },
        {
          heading: '8. Duplicate or Failed Payments',
          paragraphs: ['If an amount has been debited more than once or a payment has failed while the amount was deducted, share the order details and transaction reference. We will verify the payment status and coordinate with the relevant payment provider where necessary.']
        },
        {
          heading: '9. Refund Status and Follow-Up',
          paragraphs: ['For refund-related enquiries, contact customer support and include your order number, registered mobile number and refund reference, if available.']
        },
        {
          heading: '10. Policy Changes',
          paragraphs: ['Budget Bazaar may update this Refund Policy from time to time. Changes will be published on this page. The policy applicable to a transaction will be considered subject to applicable law and any commitments already made to the customer.']
        }
      ]
    },

    cancellation: {
      title: 'Cancellation Policy',
      effectiveDate: '22 October 2026',
      intro: 'Budget Bazaar understands that customers may occasionally need to cancel an order. This policy explains how order cancellation requests are handled for purchases made through our website.',
      sections: [
        {
          heading: '1. Customer-Initiated Cancellation',
          paragraphs: ['Customers may request cancellation before an order has been dispatched or otherwise reached a stage where cancellation is no longer reasonably possible. Contact customer support as soon as possible and provide your order number and registered contact details. Submitting a cancellation request does not automatically mean cancellation has been approved. Our team will confirm the order status and notify you of the outcome.']
        },
        {
          heading: '2. Orders Already Dispatched',
          paragraphs: ['Once an order has been dispatched or handed over to a delivery partner, cancellation may no longer be possible through the normal cancellation process. Contact support to understand available options, which may include refusal of delivery or an eligible return, subject to product type, delivery arrangements and applicable law.']
        },
        {
          heading: '3. Cancellation by Budget Bazaar',
          paragraphs: ['We may cancel an order for legitimate reasons, including:'],
          bullets: [
            'The product is unavailable or out of stock.',
            'The displayed price or product information contains a material error.',
            'The delivery address is incomplete, inaccurate or outside the serviceable area.',
            'Payment cannot be verified or the transaction is suspected to be fraudulent.',
            'An operational issue prevents us from fulfilling the order.'
          ]
        },
        {
          heading: '4. Refunds for Cancelled Orders',
          paragraphs: ['For prepaid orders, approved refunds will generally be initiated through the original payment method where supported. The time taken for funds to appear depends on the relevant bank or payment provider. For Cash on Delivery orders where no payment has been collected, no payment refund is ordinarily required. If payment was collected through another arrangement, the refund will be handled according to the circumstances. Please refer to our Refund Policy for further information.']
        },
        {
          heading: '5. Partial Order Cancellation',
          paragraphs: ['If an order contains multiple products, cancellation of an individual item may be possible if the order has not been processed beyond the point at which the item can be separated. If partial cancellation is unavailable, our support team will explain the available options. Any refund for a cancelled item will reflect the applicable amount after considering relevant discounts, taxes and charges, subject to applicable law.']
        },
        {
          heading: '6. Incorrect or Damaged Products',
          paragraphs: ['If you receive an incorrect, damaged, defective or materially misdescribed product, contact us promptly with your order number and supporting information. Such cases will be reviewed under our Refund Policy and any applicable return or replacement arrangements. They will not automatically be treated as ordinary change-of-mind cancellations.']
        },
        {
          heading: '7. Preventing Unwanted Orders',
          paragraphs: ['Before confirming your purchase, check product details, quantities, delivery address and payment information carefully. If you notice an error after placing an order, contact customer support immediately so we can determine whether the order can still be stopped or corrected.']
        },
        {
          heading: '8. Policy Updates',
          paragraphs: ['Budget Bazaar may update this Cancellation Policy periodically. The latest version will be available on this page. Changes will be applied subject to applicable law and existing customer rights.']
        }
      ]
    }
  };

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
  this.routeSubscription = this.route.url.subscribe(() => {
    const path = this.route.snapshot.routeConfig?.path;

    const policyMap: Record<string, string> = {
      'terms-and-conditions': 'terms',
      'privacy-policy': 'privacy',
      'refund-policy': 'refund',
      'cancellation-policy': 'cancellation'
    };

    this.policyKey = policyMap[path || ''] || 'terms';
    this.policy = this.policies[this.policyKey];
  });
}

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }
}
